<template>
  <ModalDialog title="Compartilhar" width="700px" v-model="isOpen">
    <div class="row">
      <div class="col">
        <img :src="article.cover_image" width="100%" class="image mb-4" />
        <PrimaryButton class="w-100 mb-3" @click="generateTitles()" :disabled="loadingTitles">
          {{ loadingTitles ? 'Gerando títulos...' : 'Gerar + títulos' }}
        </PrimaryButton>

        <div v-if="generatedTitles.length" class="generated-titles mb-3">
          <h6
            v-for="(title, index) in generatedTitles"
            :key="index"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-animation="true"
            data-bs-html="true"
            v-tooltip
            title="Copiar título"
            class="title mb-2 pb-2 divisor"
            @click="copyTitle(title)"
          >
            {{ title }}
          </h6>
        </div>


        <h6 data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="true" data-bs-html="true" v-tooltip title="Copiar título" class="title" @click="copyTitle(article.title)">{{ article.title }}</h6>
        <p class="mb-3">{{ article.subtitle }}</p>
        <small class="mb-1 d-block">
          <i class="fa-regular fa-calendar me-1"></i>
          {{ $dateFormat(article.published_at) }}
        </small>


            <PrimaryButton class="w-100 mt-4" @click="generateUrl()">
              <i class="fa-solid fa-link"></i>
              <span class="d-none d-md-inline ms-2">Copiar link</span>
            </PrimaryButton>

            <PrimaryButton class="w-100 mt-2" @click="generateImage()">
              <i class="fa-solid fa-image"></i>
              <span class="d-none d-md-inline ms-2">Gerar imagem</span>
            </PrimaryButton>

            <PrimaryButton class="w-100 red mt-2" @click="openReportModal(article.id, article.title)">
              <i class="fa-solid fa-circle-exclamation"></i>
              <span class="d-none d-md-inline ms-2">Reportar</span>
            </PrimaryButton>


      </div>

      <div v-if="article.pages && article.pages.length" class="col-6">

        <ul id="select_page">
          <li v-for="(page, key) in article.pages" :key="key" :class="page.shared == true ? 'shared' : ''" @click="generateUrl(page.id, key)">
            {{ page.name }}
            <small v-if="page.shared_at" class="shared_at">{{ $dateFormat(page.shared_at) }}</small>
          </li>
        </ul>
      </div>
    </div>
  </ModalDialog>

  <ModalDialog title="Gerar imagem" width="980px" fullscreen-mobile v-model="showImageGenerationModal">
    <ImageGeneration :article="article" />
  </ModalDialog>

  <ModalDialog title="Reportar notícia" v-model="showReportModal">
    <p>{{ reportArticleTitle }}</p>
    <TextArea style="height: 150px" v-model="reportContent" :error="formReportErrors.content" class="mt-3" />
    <PrimaryButton @click="sendReport" class="mt-4 w-100">Enviar</PrimaryButton>
  </ModalDialog>
</template>

<script setup>
import ModalDialog from '@/components/ModalDialog.vue';
import PrimaryButton from '@/components/PrimaryButton.vue';
import ImageGeneration from '@/components/ImageGeneration.vue';
import TextArea from '@/components/TextArea.vue';
import { ref, computed, inject, watch, onMounted, defineProps, defineEmits } from 'vue';
import { useArticleStore } from '@/stores/ArticleStore';
import { useAuthStore } from '@/stores/AuthStore';
import { useToast, TYPE } from "vue-toastification";
import { useArticleActions } from '@/composables/useArticleActions';
import moment from 'moment';
import { useImageTemplateStore } from '@/stores/ImageTemplateStore';
import { preloadFonts } from '@/image-generator/fonts';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  article: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['update:modelValue', 'shared', 'pageShared']);

const loader = inject('$loader');
const toast = useToast();
const articleStore = useArticleStore();
const authStore = useAuthStore();
const {
  showReportModal,
  reportArticleTitle,
  reportContent,
  formReportErrors,
  copyTitle,
  openReportModal,
  sendReport
} = useArticleActions();

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const showImageGenerationModal = ref(false);

/* Pré-carrega modelos e fontes com a página, para o gerador de imagem abrir na hora. */
onMounted(() => {
  useImageTemplateStore().getTemplates();
  preloadFonts();
});
const generatedTitles = ref([]);
const loadingTitles = ref(false);

watch(() => props.article.id, () => {
  generatedTitles.value = [];
});

async function generateUrl(page = null, pageKey = null) {
  let url = props.article.url + '?dv=' + authStore.user.hash;

  if (page) {
    url += '&pg=' + page;
  }

  await navigator.clipboard.writeText(url);
  toast('Link gerado e copiado', { type: TYPE.SUCCESS, timeout: 6000, pauseOnFocusLoss: false });

  const sharedAt = moment().format("YYYY-MM-DD HH:mm:ss");

  let params = {
    id_article: props.article.id,
  };

  if (page) {
    emit('pageShared', { pageKey, sharedAt });
    params.id_page = page;
  } else {
    emit('shared', { sharedAt });
  }

  articleStore.generateUrl(params);
}

function generateImage() {
  showImageGenerationModal.value = true;
  isOpen.value = false;
}

async function generateTitles() {
  if (loadingTitles.value) return;

  loadingTitles.value = true;
  generatedTitles.value = [];
  loader.show();

  try {
    const result = await articleStore.generateTitles(props.article.id);

    if (result.success && result.titles) {
      generatedTitles.value = result.titles;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Error generating titles:', error);
    toast('Erro ao gerar títulos', { type: TYPE.ERROR, timeout: 6000 });
  } finally {
    loadingTitles.value = false;
    loader.hide();
  }
}
</script>

<style scoped>
small {
  color: #707070;
}

.title {
  cursor: pointer;
}

.title:hover {
  opacity: 0.7;
}

.divisor {
  border-bottom: 1px solid #eee;
}

.dark .divisor {
  border-bottom: 1px solid #333;
}

.generated-titles {
  max-height: 200px;
  overflow-y: auto;
}

.image {
  border-radius: 10px;
}

#select_page {
  padding: 20px 15px;
  margin: 0;
  overflow-x: hidden;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: inherit;
  border-left: 1px #d7d7d7 solid;
}

.dark #select_page {
  border-color: #414556;
}

#select_page li {
  padding: 15px;
  list-style: none;
  background-color: #ededed;
  border-radius: 2px;
  margin-top: 4px;
  font-weight: 500;
  cursor: pointer;
}

.dark #select_page li {
  background-color: #1c2028;
}

#select_page li:first-child {
  margin-top: 0;
}

#select_page li:hover {
  background-color: #ccc;
}

.dark #select_page li:hover {
  background-color: #232832;
}

#select_page li.shared {
  background-color: #4da63f !important;
  color: #fff;
}

.shared_at {
  color: #fff;
  display: block;
  font-size: 11px;
  margin-top: -4px;
}

.dark .shared_at {
  color: #fff !important;
  opacity: 0.7;
}
</style>
